import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BadgeCheck } from "lucide-react";
import { mediaUrl } from "@/lib/api";

export function Avatar({ user, size = 40, ring = false }: { user: any; size?: number; ring?: boolean }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [user?.avatar_url]);
  const initial = (user?.display_name || user?.username || "?").charAt(0).toUpperCase();
  const inner = user?.avatar_url && !broken ? (
    <img src={mediaUrl(user.avatar_url)} alt={user.display_name || ""} onError={() => setBroken(true)} style={{ width: size, height: size }} className="object-cover rounded-full shrink-0 bg-white/45" />
  ) : (
    <div style={{ width: size, height: size, fontSize: size * 0.4 }} className="shrink-0 rounded-full bg-gradient-to-br from-[hsl(170_22%_42%)] to-[hsl(196_24%_40%)] text-white font-display font-semibold flex items-center justify-center">{initial}</div>
  );
  return ring ? <div className="ring-grad shrink-0"><div className="rounded-full p-[2px] bg-background">{inner}</div></div> : inner;
}

export function DemoChip() {
  return <span className="chip" title="Profilo fittizio gestito da UNSEEN per mostrare l'esperienza" data-testid="demo-chip"><BadgeCheck size={12} />Profilo dimostrativo</span>;
}

export function Spinner() {
  return <div className="w-6 h-6 rounded-full border-2 border-ink/15 border-t-primary animate-spin" data-testid="loading-spinner" />;
}

export function useBack(parent: string) {
  const nav = useNavigate();
  return () => ((window.history.state?.idx ?? 0) > 0 ? nav(-1) : nav(parent, { replace: true }));
}

export function BackButton({ parent = "/", className = "" }: { parent?: string; className?: string }) {
  const back = useBack(parent);
  return (
    <button onClick={back} aria-label="Indietro" data-testid="back-btn"
      className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-white/45 border border-ink/10 backdrop-blur hover:bg-white/60 active:scale-95 transition-[transform,background-color] ${className}`}>
      <ArrowLeft size={18} />
    </button>
  );
}

export function PageHeader({ title, kicker, right, back }: { title: string; kicker?: string; right?: React.ReactNode; back?: string }) {
  return (
    <header className="px-5 md:px-10 pt-6 md:pt-10 pb-5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        {back && <BackButton parent={back} />}
        <div className="min-w-0">
          {kicker && <div className="label text-primary/90 mb-1">{kicker}</div>}
          <h1 className="font-display text-2xl sm:text-3xl font-bold truncate">{title}</h1>
        </div>
      </div>
      {right}
    </header>
  );
}

export function Empty({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return <div data-testid={testId} className="rounded-2xl border border-dashed border-ink/12 p-8 text-ink/66 text-sm">{children}</div>;
}
